import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Building2, FileBadge, Wallet, Trash2, Save, Pencil } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  requested: "border-blue-200 bg-blue-50 text-blue-700",
  query: "border-amber-200 bg-amber-50 text-amber-700",
  approved: "border-emerald-200 bg-emerald-50 text-emerald-700",
  rejected: "border-rose-200 bg-rose-50 text-rose-700",
  settled: "border-violet-200 bg-violet-50 text-violet-700",
};

export default function Panel() {
  const [companies, setCompanies] = useState([]);
  const [preauths, setPreauths] = useState([]);
  const load = async () => {
    const [c, p] = await Promise.all([api.get("/panel/companies"), api.get("/panel/preauths")]);
    setCompanies(c.data.items); setPreauths(p.data.items);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <div className="section-label">Panel · Insurance · TPA</div>
        <h1 className="font-heading text-3xl font-semibold">Panel & insurance</h1>
      </div>
      <Tabs defaultValue="preauths">
        <TabsList>
          <TabsTrigger value="preauths" data-testid="panel-tab-preauths"><FileBadge size={14} className="mr-2" />Pre-auth / claims</TabsTrigger>
          <TabsTrigger value="companies" data-testid="panel-tab-companies"><Building2 size={14} className="mr-2" />Companies</TabsTrigger>
        </TabsList>

        <TabsContent value="preauths" className="mt-4">
          <PreauthsTab companies={companies} items={preauths} reload={load} />
        </TabsContent>

        <TabsContent value="companies" className="mt-4">
          <CompaniesTab items={companies} reload={load} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CompaniesTab({ items, reload }) {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const empty = { name: "", company_type: "insurance", contact_person: "", contact_phone: "", contact_email: "", address: "" };
  const [form, setForm] = useState(empty);

  const openCreate = () => { setEditingId(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      name: c.name || "",
      company_type: c.company_type || "insurance",
      contact_person: c.contact_person || "",
      contact_phone: c.contact_phone || "",
      contact_email: c.contact_email || "",
      address: c.address || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (editingId) {
        await api.patch(`/panel/companies/${editingId}`, form);
        toast.success("Company updated");
      } else {
        await api.post("/panel/companies", form);
        toast.success("Company added");
      }
      setOpen(false); setEditingId(null); setForm(empty); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete company "${c.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/panel/companies/${c.id}`);
      toast.success(`Deleted "${c.name}"`);
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(empty); } }}>
          <DialogTrigger asChild><Button className="bg-brand hover:bg-brand-700" data-testid="company-new-btn" onClick={openCreate}><Plus size={14} className="mr-2" />Add company</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">{editingId ? "Edit" : "New"} panel / insurance company</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="company-name" /></div>
              <div>
                <Label>Type</Label>
                <Select value={form.company_type} onValueChange={v => setForm({...form, company_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="insurance">Insurance</SelectItem><SelectItem value="tpa">TPA</SelectItem><SelectItem value="corporate">Corporate</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Contact person</Label><Input value={form.contact_person} onChange={e => setForm({...form, contact_person: e.target.value})} /></div>
                <div><Label>Phone</Label><Input value={form.contact_phone} onChange={e => setForm({...form, contact_phone: e.target.value})} /></div>
              </div>
              <div><Label>Email</Label><Input value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})} /></div>
              <div><Label>Address</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
              <Button onClick={save} className="bg-brand hover:bg-brand-700" data-testid="company-save">{editingId ? "Update" : "Save"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted"><tr><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Type</th><th className="px-3 py-2 text-left">Contact</th><th className="px-3 py-2 text-right">Actions</th></tr></thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && <tr><td colSpan="4" className="px-3 py-10 text-center text-ink-muted">No companies.</td></tr>}
            {items.map(c => (
              <tr key={c.id} data-testid={`company-row-${c.id}`}>
                <td className="px-3 py-2 font-medium">{c.name}</td>
                <td className="px-3 py-2 capitalize">{c.company_type}</td>
                <td className="px-3 py-2 text-xs">{[c.contact_person, c.contact_phone, c.contact_email].filter(Boolean).join(" · ") || "—"}</td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    <RateCardButton company={c} />
                    <Button size="sm" variant="ghost" onClick={() => openEdit(c)} title="Edit" data-testid={`company-edit-${c.id}`}><Pencil size={13} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(c)} title="Delete" data-testid={`company-delete-${c.id}`}><Trash2 size={13} className="text-rose-600" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function PreauthsTab({ companies, items, reload }) {
  const [open, setOpen] = useState(false);
  const [patientQ, setPatientQ] = useState("");
  const [patients, setPatients] = useState([]);
  const [form, setForm] = useState({ patient_id: "", patient_name: "", company_id: "", policy_no: "", sum_insured: 0, estimated_amount: 0, diagnosis: "", procedure: "", notes: "" });
  const search = async (q) => { setPatientQ(q); if (q.length < 2) { setPatients([]); return; } const { data } = await api.get("/patients", { params: { q, limit: 8 } }); setPatients(data.items); };
  const save = async () => {
    if (!form.patient_id || !form.company_id) { toast.error("Patient & company required"); return; }
    try { await api.post("/panel/preauths", form); toast.success("Pre-auth created"); setOpen(false); reload();
      setForm({ patient_id: "", patient_name: "", company_id: "", policy_no: "", sum_insured: 0, estimated_amount: 0, diagnosis: "", procedure: "", notes: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const update = async (id, patch) => { await api.patch(`/panel/preauths/${id}`, patch); reload(); };
  const remove = async (p) => {
    if (!window.confirm(`Delete pre-auth for ${p.patient_name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/panel/preauths/${p.id}`);
      toast.success("Pre-auth deleted");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-brand hover:bg-brand-700" data-testid="preauth-new-btn"><Plus size={14} className="mr-2" />New pre-auth</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">New pre-authorization</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              <div>
                <Label>Patient</Label>
                {form.patient_id ? <div className="flex items-center justify-between rounded-md border border-border p-2">{form.patient_name}<Button size="sm" variant="outline" onClick={() => setForm({...form, patient_id: "", patient_name: ""})}>Change</Button></div> : (
                  <>
                    <Input value={patientQ} onChange={e => search(e.target.value)} placeholder="Search…" />
                    {patients.length > 0 && (
                      <div className="mt-1 max-h-48 overflow-auto rounded-md border border-border">
                        {patients.map(p => <button key={p.id} className="block w-full border-b border-border px-3 py-2 text-left text-sm hover:bg-secondary" onClick={() => { setForm(f => ({...f, patient_id: p.id, patient_name: p.name})); setPatients([]); setPatientQ(""); }}>{p.name} <span className="text-xs text-ink-muted">{p.uhid}</span></button>)}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div>
                <Label>Company</Label>
                <Select value={form.company_id} onValueChange={v => setForm({...form, company_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Policy no.</Label><Input value={form.policy_no} onChange={e => setForm({...form, policy_no: e.target.value})} /></div>
                <div><Label>Sum insured (₹)</Label><Input type="number" value={form.sum_insured} onChange={e => setForm({...form, sum_insured: Number(e.target.value)})} /></div>
              </div>
              <div><Label>Estimated amount (₹)</Label><Input type="number" value={form.estimated_amount} onChange={e => setForm({...form, estimated_amount: Number(e.target.value)})} /></div>
              <div><Label>Diagnosis</Label><Input value={form.diagnosis} onChange={e => setForm({...form, diagnosis: e.target.value})} /></div>
              <div><Label>Procedure</Label><Input value={form.procedure} onChange={e => setForm({...form, procedure: e.target.value})} /></div>
              <Button onClick={save} className="bg-brand hover:bg-brand-700" data-testid="preauth-save">Submit</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted"><tr><th className="px-3 py-2 text-left">Patient</th><th className="px-3 py-2 text-left">Company</th><th className="px-3 py-2 text-left">Procedure</th><th className="px-3 py-2 text-right">Estimate</th><th className="px-3 py-2 text-right">Approved</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-right">Action</th></tr></thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && <tr><td colSpan="7" className="px-3 py-10 text-center text-ink-muted">No pre-auth requests yet.</td></tr>}
            {items.map(p => (
              <tr key={p.id}>
                <td className="px-3 py-2"><div className="font-medium">{p.patient_name}</div><div className="text-[11px] font-mono text-ink-muted">{p.patient_uhid}</div></td>
                <td className="px-3 py-2">{p.company_name}</td>
                <td className="px-3 py-2">{p.procedure || "—"}</td>
                <td className="px-3 py-2 text-right">₹ {Number(p.estimated_amount).toLocaleString("en-IN")}</td>
                <td className="px-3 py-2 text-right">₹ {Number(p.approved_amount || 0).toLocaleString("en-IN")}</td>
                <td className="px-3 py-2"><Badge variant="outline" className={STATUS[p.status] || ""}>{p.status}</Badge></td>
                <td className="px-3 py-2 space-x-1 text-right">
                  {p.status === "requested" && <>
                    <Button size="sm" variant="outline" onClick={() => { const amt = prompt("Approved amount (₹):", p.estimated_amount); if (amt != null) update(p.id, { status: "approved", approved_amount: Number(amt) }); }}>Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => update(p.id, { status: "rejected" })}>Reject</Button>
                  </>}
                  {p.status === "approved" && <Button size="sm" variant="outline" onClick={() => { const amt = prompt("Settled amount (₹):", p.approved_amount); if (amt != null) update(p.id, { status: "settled", settled_amount: Number(amt) }); }}>Settle</Button>}
                  {p.status !== "settled" && (
                    <Button size="sm" variant="ghost" onClick={() => remove(p)} title="Delete pre-auth" data-testid={`preauth-delete-${p.id}`}>
                      <Trash2 size={13} className="text-rose-600" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}


function RateCardButton({ company }) {
  const [open, setOpen] = useState(false);
  const [services, setServices] = useState([]);   // available service/package masters
  const [rows, setRows] = useState([]);            // rate-card rows: {item_code, name, item_type, rate, discount_pct}
  const [serviceQuery, setServiceQuery] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [{ data: rc }, { data: sm }] = await Promise.all([
        api.get(`/panel/companies/${company.id}/rate-card`),
        api.get(`/billing/service-masters?active=true`),
      ]);
      setRows(rc.items || []);
      setServices(sm.items || []);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const addServiceRow = (svc) => {
    if (rows.find(r => r.item_code === svc.code)) {
      toast.info("Already on the rate-card.");
      return;
    }
    setRows([...rows, {
      item_code: svc.code,
      name: svc.name,
      item_type: svc.type || "service",
      rate: svc.rate || 0,
      discount_pct: 0,
    }]);
    setServiceQuery("");
  };

  const updateRow = (i, key, value) => setRows(rows.map((r, idx) => idx === i ? { ...r, [key]: value } : r));
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        items: rows.map(r => ({
          item_code: r.item_code,
          name: r.name,
          item_type: r.item_type || "service",
          rate: parseFloat(r.rate) || 0,
          discount_pct: parseFloat(r.discount_pct) || 0,
        })),
      };
      await api.put(`/panel/companies/${company.id}/rate-card`, payload);
      toast.success(`Rate-card saved (${payload.items.length} items)`);
      setOpen(false);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const filteredServices = useMemo(() => {
    if (!serviceQuery) return [];
    const q = serviceQuery.toLowerCase();
    return services
      .filter(s => s.name.toLowerCase().includes(q) || (s.code || "").toLowerCase().includes(q))
      .filter(s => !rows.find(r => r.item_code === s.code))
      .slice(0, 8);
  }, [serviceQuery, services, rows]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`company-rate-card-${company.id}`}>
          <Wallet size={11} className="mr-1" />Rate-card
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Rate-card — {company.name}</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-ink-muted">
          Map services & packages with panel-specific rates. When an IPD/Day-care patient is admitted under this
          panel, picking any of these services will auto-apply the override rate (and optional %-discount).
          Items not on this card use the standard service-master rate.
        </div>

        {/* Service master picker */}
        <div className="relative" data-testid="rc-svc-picker">
          <Label className="text-[10px]">Add service / package from masters</Label>
          <Input
            value={serviceQuery}
            onChange={e => setServiceQuery(e.target.value)}
            placeholder="Search by name or code (e.g. Phaco, OCT, BED)…"
            className="h-9"
            data-testid="rc-svc-search"
          />
          {filteredServices.length > 0 && (
            <div className="absolute left-0 right-0 z-20 mt-1 max-h-72 overflow-y-auto rounded-md border border-border bg-white shadow-lg">
              {filteredServices.map(s => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => addServiceRow(s)}
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-secondary"
                  data-testid={`rc-svc-pick-${s.code}`}
                >
                  <div>
                    <div className="font-medium">{s.name}</div>
                    <div className="text-[10px] text-ink-muted"><span className="font-mono">{s.code}</span> · {s.type}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold">₹ {(s.rate || 0).toLocaleString("en-IN")}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Rate-card table */}
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-[10px] uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-2 py-1.5 text-left">Code</th>
                <th className="px-2 py-1.5 text-left">Service / Package</th>
                <th className="px-2 py-1.5 text-left">Type</th>
                <th className="px-2 py-1.5 text-right">Panel rate (₹)</th>
                <th className="px-2 py-1.5 text-right">Disc %</th>
                <th className="px-2 py-1.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-2 py-6 text-center text-ink-muted">
                  No items yet. Use the search above to add services / packages from your masters.
                </td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={r.item_code} data-testid={`rc-row-${r.item_code}`}>
                  <td className="px-2 py-1 font-mono text-[10px]">{r.item_code}</td>
                  <td className="px-2 py-1">{r.name}</td>
                  <td className="px-2 py-1 capitalize text-[11px]">{r.item_type}</td>
                  <td className="px-2 py-1 text-right">
                    <Input
                      type="number"
                      value={r.rate}
                      onChange={e => updateRow(i, "rate", e.target.value)}
                      className="h-7 w-24 text-right text-xs"
                      data-testid={`rc-rate-${r.item_code}`}
                    />
                  </td>
                  <td className="px-2 py-1 text-right">
                    <Input
                      type="number"
                      value={r.discount_pct}
                      onChange={e => updateRow(i, "discount_pct", e.target.value)}
                      className="h-7 w-16 text-right text-xs"
                      data-testid={`rc-disc-${r.item_code}`}
                    />
                  </td>
                  <td className="px-2 py-1 text-right">
                    <Button size="sm" variant="ghost" onClick={() => removeRow(i)} data-testid={`rc-remove-${r.item_code}`}>
                      <Trash2 size={11} className="text-rose-600" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-3 flex justify-end">
          <Button onClick={save} disabled={busy} className="bg-brand hover:bg-brand-700" data-testid="rc-save-btn">
            <Save size={14} className="mr-2" />Save rate-card
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
